/* ============================================================================
 * ตัวเลือกกลางของ "ผู้ใช้งาน" (users.js) และ "บทบาท" (roles.js)
 *
 * รวมไว้ที่เดียวเพราะสองหน้าใช้ชุดข้อมูลเดียวกัน — หน้าผู้ใช้ต้องเลือกบทบาท
 * ส่วนหน้าบทบาทต้องนับจำนวนผู้ใช้ในแต่ละบทบาท ถ้าแยกกันประกาศจะหลุดกันได้ง่าย
 *
 * ข้อมูลผู้ใช้/บทบาทมาจาก API แล้ว (admin/users, admin/roles) ไฟล์นี้เหลือเฉพาะ
 * ตัวเลือกคงที่และตัวช่วยแสดงผล
 * USER_TYPES / TEAMS ต้องตรงกับ Munera-Backend/modules/admin/user/user.constant.js
 * โครง permission ใช้รูปแบบเดียวกับ global.permission : "<module>.<action>"
 * ========================================================================== */

/* --------------------------------------------------------- ประเภทผู้ใช้งาน */
/* tone/tint = สีประจำประเภท ผ่านการตรวจ contrast บนพื้นขาวแล้ว */
export const USER_TYPES = [
  { value: "super_user", label: "user_type_super_user", icon: "", tone: "#4338ca", tint: "#e0e7ff" },
  { value: "admin", label: "user_type_admin", icon: "", tone: "#0f766e", tint: "#ccfbf1" },
  /* พาร์ตเนอร์ — บัญชีที่ระบบออกให้ตอนอนุมัติใบสมัครในเมนูพาร์ตเนอร์
     ตั้งจากฟอร์มเพิ่ม/แก้ผู้ใช้ไม่ได้ (backend ปฏิเสธด้วย MANAGED_USER_TYPES)
     มีไว้ที่นี่เพื่อให้ตารางผู้ใช้งานแสดงป้ายประเภทได้ถูก ไม่ใช่เพื่อให้เลือก —
     ตัวเลือกใน dropdown กรองด้วย SELECTABLE_USER_TYPES ด้านล่าง */
  { value: "partner", label: "user_type_partner", icon: "\uf2b5", tone: "#b45309", tint: "#fef3c7" },
];

export const PARTNER_USER_TYPE = "partner";

/* ประเภทที่เลือกได้จริงในฟอร์มเพิ่ม/แก้ผู้ใช้ — ต้องตรงกับ MANAGED_USER_TYPES
   ฝั่ง backend (Munera-Backend/modules/admin/user/user.constant.js) */
export const SELECTABLE_USER_TYPES = USER_TYPES.filter((item) => item.value !== PARTNER_USER_TYPE);

export const isPartnerUser = (item) => item?.user_type === PARTNER_USER_TYPE;

/* แท็บของหน้าผู้ใช้งาน — บัญชีพาร์ตเนอร์เป็นคนละชนิดงานกับทีมงานภายใน :
   เกิดจากการอนุมัติใบสมัคร ไม่ได้สร้างจากเมนูนี้ สถานะผูกกับสถานะใบสมัคร และ
   ปกติมีจำนวนมากกว่าทีมงานหลายเท่า ปนอยู่รายการเดียวกันจึงหาคนในทีมไม่เจอ

   แท็บทีมงานกรองแบบ "ไม่เอาพาร์ตเนอร์" (exclude) ไม่ใช่ระบุรายชื่อประเภทที่ต้องการ
   เพราะบัญชีที่มีประเภทเก่านอกรายการ USER_TYPES จะได้ยังเห็นอยู่ ไม่หายไปทั้งสองแท็บ */
export const USER_TABS = [
  { key: "staff", label: "user_tab_staff", icon: "\uf0c0", exclude: [PARTNER_USER_TYPE] },
  { key: "partner", label: "user_tab_partner", icon: "\uf2b5", types: [PARTNER_USER_TYPE] },
];

export const tabMeta = (key) => USER_TABS.find((item) => item.key === key) || USER_TABS[0];

/* อีกแท็บหนึ่ง — ใช้ดึงตัวเลขบนป้ายแท็บที่ยังไม่ได้เปิดอยู่ (มีสองแท็บเท่านั้น) */
export const otherTab = (key) => USER_TABS.find((item) => item.key !== key) || USER_TABS[0];

/* ประเภทที่ไม่รู้จัก (ข้อมูลเก่า) แสดงเป็นขีดกลาง ไม่เดาเป็นประเภทอื่น */
const UNKNOWN_TYPE = { value: "", label: "-", icon: "", tone: "#94a3b8", tint: "#e2e8f0" };

export const typeMeta = (value) => USER_TYPES.find((e) => e.value === value) || UNKNOWN_TYPE;

/* --------------------------------------------------------------- ทีม / สาขา */
/* ตอนนี้เปิดใช้สำนักงานใหญ่ที่เดียว เพิ่มสาขาต้องเพิ่มที่ user.constant.js ฝั่ง backend ด้วย */
export const TEAMS = [
  { value: "hq", label: "team_hq" },
];

export const teamMeta = (value) => TEAMS.find((e) => e.value === value);

/* ------------------------------------------------------------- สิทธิ์ (CRUD) */
export const ACTIONS = [
  { key: "read", label: "view", icon: "" },
  { key: "create", label: "create", icon: "" },
  { key: "update", label: "edit", icon: "" },
  { key: "delete", label: "delete_action", icon: "" },
  /* approve ใช้อยู่โมดูลเดียว (พาร์ตเนอร์) แต่ต้องอยู่ในรายการนี้เพื่อให้ตารางสิทธิ์
     มีคอลัมน์ให้ติ๊ก — โมดูลที่ไม่รองรับจะขึ้นเป็นขีดกลางเองตามกลไกเดิมของตาราง
     แยกจาก update เพราะการอนุมัติคือการเปิดบัญชีเข้าใช้งานให้คนนอก ไม่ใช่ระดับ
     เดียวกับการแก้ข้อมูลในใบสมัคร */
  { key: "approve", label: "approve_action", icon: "\uf00c" },
];

/* โมดูลทั้งหมดที่กำหนดสิทธิ์ได้ — key ต้องตรงกับ global.permission
   actions = สิทธิ์ที่โมดูลนั้นรองรับ (แดชบอร์ด/รายงานมีแค่ดูข้อมูล)

   ลำดับเรียงตาม global.menu_admin (แถบเมนูซ้าย) เพื่อให้ตารางสิทธิ์อ่านคู่กับเมนูได้ :
   แดชบอร์ด › ทรัพย์ทั้งหมด (ทรัพย์ / โครงการ / ประเภททรัพย์) › การตลาด (กระดานงาน /
   ช่องทาง) › ลูกค้า › นัดชมทรัพย์ › พาร์ตเนอร์ › เติมเหรียญ › รายงาน ›
   ตั้งค่า (ข้อมูลบริษัท / ลายน้ำ /
   ตั้งค่าเหรียญ / ผู้ใช้งาน / บทบาท) › จัดการหน้าแรก
   ถ้าเพิ่มหรือสลับเมนูใน global.menu_admin ให้ปรับลำดับที่นี่ตามด้วย

   ทุก key ที่อยู่ในตารางนี้ต้องมีแถวอยู่ในตาราง permissions ฝั่ง backend ด้วย
   (migration 20260808000100-seed_permission_catalog) ไม่งั้นกดบันทึกบทบาทจะไม่ผ่าน */
export const PERMISSION_MODULES = [
  { key: "dashboard-admin", label: "dashboard", icon: "\uf0e4", actions: ["read"] },
  { key: "assets", label: "all_properties", icon: "\uf015", actions: ["read", "create", "update", "delete"] },
  { key: "projects", label: "projects", icon: "\uf1b3", actions: ["read", "create", "update", "delete"] },
  { key: "asset-categories", label: "property_categories", icon: "\uf02c", actions: ["read", "create", "update", "delete"] },
  { key: "marketing", label: "marketing_board", icon: "\uf0a1", actions: ["read", "create", "update", "delete"] },
  { key: "marketing-channels", label: "marketing_channels", icon: "\uf1e0", actions: ["read", "create", "update", "delete"] },
  { key: "customers", label: "customers", icon: "\uf0c0", actions: ["read", "create", "update", "delete"] },
  { key: "appointments", label: "appointments", icon: "\uf073", actions: ["read", "create", "update", "delete"] },
  /* พาร์ตเนอร์ : create ยังไม่มีเส้นทางใช้จริง (ใบสมัครเกิดจากหน้าเว็บสาธารณะเท่านั้น)
     แต่ประกาศไว้ให้ครบชุดเหมือนโมดูลอื่น เผื่อเปิดให้แอดมินคีย์ใบสมัครแทนในอนาคต */
  { key: "partners", label: "partners", icon: "\uf2b5", actions: ["read", "create", "update", "delete", "approve"] },
  /* คำขอเติมเหรียญ : ไม่มี create/delete — คำขอเกิดจากพาร์ตเนอร์เท่านั้น และใบที่
     ตัดสินไปแล้วต้องเก็บไว้ตรวจย้อนหลัง ไม่ใช่ลบทิ้ง
     approve = กดให้เหรียญเข้ากระเป๋าจริง จึงแยกจาก update (แก้หมายเหตุ/ข้อมูลในใบ) */
  { key: "wallet-topups", label: "topup_review", icon: "\uf543", actions: ["read", "update", "approve"] },
  { key: "reports", label: "reports", icon: "\uf080", actions: ["read"] },
  { key: "settings", label: "company_profile", icon: "\uf1ad", actions: ["read", "update"] },
  { key: "watermark", label: "watermark_setting", icon: "\uf03e", actions: ["read", "update"] },
  // ตั้งค่าเหรียญ — ตารางแถวเดียวของทั้งระบบ จึงมีแค่ดูกับแก้ไข เหมือนลายน้ำ
  { key: "wallet-settings", label: "wallet_setting", icon: "\uf51e", actions: ["read", "update"] },
  { key: "users", label: "users", icon: "\uf0c0", actions: ["read", "create", "update", "delete"] },
  { key: "roles", label: "roles", icon: "\uf3ed", actions: ["read", "create", "update", "delete"] },
  { key: "homepage", label: "home_manage", icon: "\uf2d0", actions: ["read", "update"] },
];

/* permission ทั้งหมดที่เปิดให้ติ๊กได้ (ใช้เป็นตัวหารตอนคิด % และปุ่มเลือกทั้งหมด) */
export const ALL_PERMISSIONS = PERMISSION_MODULES.flatMap((module) =>
  module.actions.map((action) => `${module.key}.${action}`)
);

export const modulePermissions = (module) => module.actions.map((action) => `${module.key}.${action}`);

/* หาบทบาทจาก uid ในรายการที่โหลดมาจาก API */
export const roleMeta = (uid, roles = []) => roles.find((e) => e.uid === uid);

/* --------------------------------------------------------------- ตัวช่วยแสดงผล */
/* อักษรย่อบนรูปโปรไฟล์ — ชื่อไทยใช้ตัวแรกของชื่อ+นามสกุล, อังกฤษใช้ตัวแรกสองคำ */
export const initials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "-";
  if (parts.length === 1) return parts[0].slice(0, 2);
  return `${parts[0][0]}${parts[1][0]}`;
};

/* พื้นหลังรูปโปรไฟล์ — คงที่ต่อคนโดยอ้างอิงจากชื่อ (ไม่สุ่มใหม่ทุกครั้งที่ render)
   ไล่โทนให้ครบวงล้อสี รายชื่อยาว ๆ จึงดูมีสีสันไม่จมเป็นเขียวเข้มทั้งตาราง
   ทุกคู่สีต้องเข้มพอให้อักษรย่อสีขาวอ่านออก (ทุกที่ที่ใช้ตั้ง color: #fff) */
const AVATAR_GRADIENTS = [
  "linear-gradient(150deg,#059669,#065f46)",
  "linear-gradient(150deg,#4d7c0f,#2f4a09)",
  "linear-gradient(150deg,#0f766e,#134e4a)",
  "linear-gradient(150deg,#0891b2,#155e75)",
  "linear-gradient(150deg,#2563eb,#1e3a8a)",
  "linear-gradient(150deg,#4338ca,#312e81)",
  "linear-gradient(150deg,#7c3aed,#4c1d95)",
  "linear-gradient(150deg,#a21caf,#701a75)",
  "linear-gradient(150deg,#be185d,#831843)",
  "linear-gradient(150deg,#e11d48,#881337)",
  "linear-gradient(150deg,#ea580c,#7c2d12)",
  "linear-gradient(150deg,#b45309,#78350f)",
  "linear-gradient(150deg,#475569,#1e293b)",
  "linear-gradient(150deg,#65a30d,#3f6212)",
];

/* djb2 — บวกรหัสอักขระเฉย ๆ ทำให้ชื่อที่สลับตัวอักษรกันหรือยาวใกล้กันได้สีเดียวกัน
   ชื่อไทยยิ่งชนง่ายเพราะรหัสอักขระอยู่ในช่วงแคบ hash จริงจึงกระจายสีดีกว่ามาก */
export const avatarTone = (key = "") => {
  const hash = String(key)
    .split("")
    .reduce((acc, char) => ((acc * 33) ^ char.charCodeAt(0)) >>> 0, 5381);
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

/* ------------------------------------------------------------- รูปโปรไฟล์ */
/* เพดานเท่าโลโก้บริษัท/ช่องทางการตลาด — รูปโปรไฟล์แสดงใหญ่สุดแค่ 76px
   ไฟล์ใหญ่กว่านี้ไม่ได้ทำให้คมขึ้น มีแต่ทำให้หน้าเว็บลูกค้าโหลดช้า */
export const PICTURE_MAX_SIZE = 2 * 1024 * 1024;
export const PICTURE_ACCEPT = "image/png,image/jpeg,image/webp";
/* โฟลเดอร์บน storage — แยกตามโมดูลเหมือน assets/photo และ marketing/channel */
export const PICTURE_FOLDER = "users/picture";
/* ขนาดที่แนะนำ : สี่เหลี่ยมจัตุรัส เพราะทุกที่ที่แสดงถูกครอบเป็นวงกลม/สี่เหลี่ยมมน
   400px พอสำหรับจอ 2x ของกล่องใหญ่สุด (76px) โดยไฟล์ยังเล็ก */
export const PICTURE_SIZE_HINT = "400 x 400 px";

/* --------------------------------------------------------------- LINE */
/* ลิงก์ "เพิ่มเพื่อน" — บัญชีทางการ (ขึ้นต้นด้วย @) กับไอดีส่วนตัวใช้คนละรูปแบบ
   ผู้ใช้กรอกมาแบบไหนก็ได้ รวมทั้งวางลิงก์เต็มมาเลย จึงต้องแยกให้ถูกทางที่นี่ */
export const lineAddUrl = (lineId = "") => {
  const id = String(lineId).trim();
  if (!id) return "";
  if (/^https?:\/\//i.test(id)) return id;
  if (id.startsWith("@")) return `https://line.me/R/ti/p/${encodeURIComponent(id)}`;
  return `https://line.me/ti/p/~${encodeURIComponent(id.replace(/^~/, ""))}`;
};

/* 0812345678 -> 081-234-5678 */
export const fmtPhone = (phone = "") => {
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length !== 10) return phone || "-";
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/* นับสิทธิ์ที่เปิดใช้จริง (กรองเฉพาะ permission ที่มีอยู่ในตารางสิทธิ์) */
export const countPermission = (permission = []) =>
  permission.filter((perm) => ALL_PERMISSIONS.includes(perm)).length;
